<template>
  <div class="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3 space-y-3">
    <div class="flex items-center gap-2">
      <span class="material-symbols-outlined text-sm text-gray-500">transform</span>
      <span class="text-xs font-semibold text-gray-700 dark:text-gray-300">{{
        tt('Value Transformer', 'Pretvornik vrednosti')
      }}</span>
      <span class="text-xs text-gray-400 dark:text-gray-500">{{ tt('(optional)', '(neobvezno)') }}</span>
    </div>

    <!-- Number: scale + offset -->
    <template v-if="valueType === 'number'">
      <div class="grid grid-cols-2 gap-2">
        <div>
          <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">
            {{ tt('Scale factor', 'Faktor merila') }}
            <span class="text-gray-400 dark:text-gray-500 font-normal">{{
              tt('(multiply, e.g. 0.001 for W→kW)', '(pomnožite, npr. 0.001 za W→kW)')
            }}</span>
          </label>
          <input
            v-model.number="local.scaleFactor"
            type="number"
            step="any"
            placeholder="1"
            class="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            @change="emit" />
        </div>
        <div>
          <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">
            {{ tt('Offset', 'Odmik') }}
            <span class="text-gray-400 dark:text-gray-500 font-normal">{{
              tt('(add after scale, e.g. -273.15 for K→°C)', '(dodajte po merilu, npr. -273.15 za K→°C)')
            }}</span>
          </label>
          <input
            v-model.number="local.valueOffset"
            type="number"
            step="any"
            placeholder="0"
            class="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            @change="emit" />
        </div>
      </div>
      <p class="text-xs text-gray-400 dark:text-gray-500">
        {{ tt('Result', 'Rezultat') }} = (rawValue × {{ local.scaleFactor ?? 1 }}) + {{ local.valueOffset ?? 0 }}
      </p>
    </template>

    <!-- Boolean: state mapping -->
    <template v-else-if="valueType === 'boolean'">
      <div class="grid grid-cols-2 gap-2">
        <div>
          <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">{{
            tt('True value string', 'Besedilo za vrednost true')
          }}</label>
          <input
            v-model="local.haStateTrue"
            type="text"
            placeholder="on"
            class="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
            @change="emit" />
        </div>
        <div>
          <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">{{
            tt('False value string', 'Besedilo za vrednost false')
          }}</label>
          <input
            v-model="local.haStateFalse"
            type="text"
            placeholder="off"
            class="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
            @change="emit" />
        </div>
      </div>
      <p class="text-xs text-gray-400 dark:text-gray-500">
        {{ tt('When source value equals', 'Ko je izvorna vrednost enaka') }} "<span class="font-mono">{{
          local.haStateTrue || 'on'
        }}</span
        >" → <code>true</code>; "<span class="font-mono">{{ local.haStateFalse || 'off' }}</span
        >" → <code>false</code>
      </p>
    </template>

    <!-- Enum: value mapping table -->
    <template v-else-if="valueType === 'enum'">
      <div class="space-y-2">
        <div class="flex items-center justify-between">
          <label class="text-xs text-gray-500 dark:text-gray-400">{{
            tt('Value map (source → local enum)', 'Preslikava vrednosti (vir → lokalna naštevilka)')
          }}</label>
          <button class="text-xs text-blue-500 hover:text-blue-700 dark:hover:text-blue-400" @click="addEnumMapping">
            + {{ tt('Add mapping', 'Dodaj preslikavo') }}
          </button>
        </div>
        <div v-for="(mapping, idx) in local.enumMappings" :key="idx" class="flex items-center gap-2">
          <input
            v-model="mapping.sourceValue"
            type="text"
            :placeholder="tt('source', 'vir')"
            class="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
            @change="emit" />
          <span class="material-symbols-outlined text-sm text-gray-400">arrow_forward</span>
          <input
            v-model="mapping.localValue"
            type="text"
            :placeholder="tt('local', 'lokalno')"
            class="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
            @change="emit" />
          <button class="text-red-400 hover:text-red-600" @click="removeEnumMapping(idx)">
            <span class="material-symbols-outlined text-sm">remove_circle</span>
          </button>
        </div>
        <p v-if="local.enumMappings.length === 0" class="text-xs text-gray-400">
          {{ tt('No mappings — raw value will be used', 'Ni preslikav — uporabljena bo izvorna vrednost') }}
        </p>
      </div>
    </template>

    <!-- String: no transform options -->
    <template v-else>
      <p class="text-xs text-gray-400 dark:text-gray-500">
        {{
          tt(
            'No transformation options for string type — raw value is used as-is.',
            'Za besedilni tip ni možnosti preoblikovanja — izvorna vrednost se uporabi takšna, kot je.',
          )
        }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { tt } from '../utils/consoleText';

export interface TransformerConfig {
  scaleFactor?: number;
  valueOffset?: number;
  haStateTrue?: string;
  haStateFalse?: string;
  enumMappings?: Array<{ sourceValue: string; localValue: string }>;
}

interface Props {
  modelValue?: TransformerConfig;
  valueType: string; // 'number' | 'boolean' | 'string' | 'enum'
}

const props = defineProps<Props>();
const emits = defineEmits<{
  'update:modelValue': [value: TransformerConfig];
}>();

const local = ref<{
  scaleFactor?: number;
  valueOffset?: number;
  haStateTrue: string;
  haStateFalse: string;
  enumMappings: Array<{ sourceValue: string; localValue: string }>;
}>({
  scaleFactor: undefined,
  valueOffset: undefined,
  haStateTrue: 'on',
  haStateFalse: 'off',
  enumMappings: [],
});

watch(
  () => props.modelValue,
  (val) => {
    if (!val) return;
    local.value.scaleFactor = val.scaleFactor;
    local.value.valueOffset = val.valueOffset;
    local.value.haStateTrue = val.haStateTrue ?? 'on';
    local.value.haStateFalse = val.haStateFalse ?? 'off';
    local.value.enumMappings = val.enumMappings ? [...val.enumMappings] : [];
  },
  { immediate: true },
);

function emit() {
  const out: TransformerConfig = {};
  if (props.valueType === 'number') {
    if (
      local.value.scaleFactor !== undefined &&
      local.value.scaleFactor !== null &&
      !isNaN(Number(local.value.scaleFactor))
    ) {
      out.scaleFactor = Number(local.value.scaleFactor);
    }
    if (
      local.value.valueOffset !== undefined &&
      local.value.valueOffset !== null &&
      !isNaN(Number(local.value.valueOffset))
    ) {
      out.valueOffset = Number(local.value.valueOffset);
    }
  } else if (props.valueType === 'boolean') {
    out.haStateTrue = local.value.haStateTrue || 'on';
    out.haStateFalse = local.value.haStateFalse || 'off';
  } else if (props.valueType === 'enum') {
    out.enumMappings = local.value.enumMappings.filter((m) => m.sourceValue || m.localValue);
  }
  emits('update:modelValue', out);
}

function addEnumMapping() {
  local.value.enumMappings.push({ sourceValue: '', localValue: '' });
}

function removeEnumMapping(idx: number) {
  local.value.enumMappings.splice(idx, 1);
  emit();
}
</script>
